import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, ShoppingBag, Check, Loader2, Plus, Minus } from 'lucide-react';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { toast } from '../hooks/use-toast';
import { Button } from '../components/ui/button';
import { imageUrl, euro, collectionLabels } from '../lib/catalog';
import { ProductTechnicalDetails } from '../components/ProductTechnicalDetails';
import { ShimmerButton } from '../components/magicui/shimmer-button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const previousCatalog = location.state?.catalogReturn;
  const catalogReturn = typeof previousCatalog === 'string' && /^\/webshop(?:\?|$)/.test(previousCatalog)
    ? previousCatalog : '/webshop';
  const { addToCart } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setQuantity(1);
    axios.get(`${API}/products/${id}`, { signal: controller.signal })
      .then(response => setProduct(response.data))
      .catch(err => { if (!axios.isCancel(err)) setProduct(null); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-gray-600" aria-label="Učitavanje proizvoda" data-testid="product-loading" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-semibold mb-4" data-testid="product-not-found">Proizvod nije pronađen</h2>
          <Button onClick={() => navigate('/webshop')} data-testid="product-not-found-back">Povratak na WebShop</Button>
        </div>
      </div>
    );
  }

  const handleAddToCart = () => {
    addToCart(product, quantity);
    toast({
      title: 'Dodano u košaricu',
      description: `${quantity}x ${product.name} je dodano u vašu košaricu.`,
    });
  };

  return (
    <div className="premium-page">
      <div className="premium-container">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate(catalogReturn)}
          className="mb-8"
          data-testid="product-back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Povratak na WebShop
        </Button>

        <div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
            {/* Product Image */}
            <div className="premium-detail-visual self-start lg:sticky lg:top-28">
              <img
                src={imageUrl(product.image)}
                alt={product.name}
                className="w-full object-contain"
                data-testid="product-image"
              />
            </div>

            {/* Product Info */}
            <div className="premium-detail-copy min-w-0">
              <div className="mb-4">
                <span className="premium-eyebrow" data-testid="product-category">
                  {collectionLabels[product.collection] || product.category}
                </span>
              </div>
              
              <h1 className="text-3xl sm:text-4xl font-serif mb-4 break-words" data-testid="product-name">{product.name}</h1>
              <p className="text-sm text-gray-500 mb-4" data-testid="product-sku">Šifra: {product.sku}</p>
              
              <div className="flex items-center space-x-4 mb-6">
                <span className="detail-price" data-testid="product-price">{euro(product.price)}</span>
              </div>

              <p className="text-sm text-gray-500 mb-5" data-testid="product-vat-notice">Cijena uključuje 25 % PDV-a.</p>
              <p className="text-sm text-gray-600 leading-relaxed mb-8" data-testid="product-description">
                {product.description}
              </p>

              {/* Features */}
              {!product.technicalDetails && product.features && product.features.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-lg font-semibold mb-3">Karakteristike:</h3>
                  <ul className="space-y-2">
                    {product.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2 text-gray-700 break-words text-sm" data-testid={`product-feature-${index}`}>
                        <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                        <span className="min-w-0">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Quantity Selector */}
              <div className="mb-8">
                <label className="block text-sm font-medium mb-2">Količina:</label>
                <div className="flex items-center space-x-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    aria-label="Smanji količinu"
                    data-testid="product-quantity-decrease"
                  >
                    <Minus size={16} aria-hidden="true" />
                  </Button>
                  <span className="text-xl font-semibold w-12 text-center" data-testid="product-quantity">{quantity}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setQuantity(quantity + 1)}
                    aria-label="Povećaj količinu"
                    data-testid="product-quantity-increase"
                  >
                    <Plus size={16} aria-hidden="true" />
                  </Button>
                </div>
              </div>

              {/* Add to Cart Button */}
              <ShimmerButton
                onClick={handleAddToCart}
                disabled={!product.inStock}
                className="w-full"
                data-testid="product-add-button"
              >
                <ShoppingBag className="w-4 h-4" />
                Dodaj u košaricu
              </ShimmerButton>
            </div>
          </div>
        </div>
        <ProductTechnicalDetails key={product.id} product={product} />
      </div>
    </div>
  );
};

export default ProductDetail;
